// tmux-style prefix-key pane switcher for the 3-pane layout.
//
// Listens for `Ctrl+B` as the "prefix" key. Within 500ms of pressing the
// prefix, a follow-up keystroke (arrow keys, 0/1/2, or `o`) moves focus
// between panes. Unlike a normal global hotkey, this prefix approach lets
// the inner inputs keep using ArrowUp for command-history recall — the
// switcher only swallows those keys when the prefix was just pressed.
//
// Pane layout (two chat panes side-by-side, shell row below):
//
//   ┌───────────────┬───────────────┐
//   │ [0] coach     │ [1] tech      │
//   ├───────────────┴───────────────┤
//   │ [2] shell                     │
//   └───────────────────────────────┘
//
// Returns:
//   active       : current PaneId
//   setActive    : imperative setter (used by mouse click handlers)
//   prefixActive : true for the ~200ms after Ctrl+B is pressed; the visual
//                  layer renders a tiny green dot when this is true.

import { useCallback, useEffect, useRef, useState } from 'react';

export type PaneId = 'coach' | 'tech' | 'shell';

export const PANE_ORDER: PaneId[] = ['coach', 'tech', 'shell'];

/**
 * Directional move map.
 *
 *   - coach (top-left) <-> tech (top-right) horizontally
 *   - any chat pane + Down -> shell
 *   - shell + Up returns to coach (the default anchor pane). Use prefix+1
 *     to jump to tech directly.
 *
 * All other directions are no-ops (return the current pane).
 */
function paneInDirection(from: PaneId, dir: 'left' | 'right' | 'up' | 'down'): PaneId {
  switch (dir) {
    case 'left':
      if (from === 'tech') return 'coach';
      return from;
    case 'right':
      if (from === 'coach') return 'tech';
      return from;
    case 'up':
      if (from === 'shell') return 'coach';
      return from;
    case 'down':
      if (from === 'coach' || from === 'tech') return 'shell';
      return from;
  }
}

export interface UseTmuxPaneSwitcherResult {
  active: PaneId;
  setActive: (p: PaneId) => void;
  prefixActive: boolean;
}

const PREFIX_TIMEOUT_MS = 500;
const PREFIX_FLASH_MS = 200;

export function useTmuxPaneSwitcher(initial: PaneId = 'coach'): UseTmuxPaneSwitcherResult {
  const [active, setActive] = useState<PaneId>(initial);
  const [prefixActive, setPrefixActive] = useState<boolean>(false);

  // Whether the prefix window is currently open (i.e. the next keystroke
  // should be interpreted as a pane-switch action). Stored in a ref because
  // the keydown handler is a stable function that should not re-bind on
  // every state change.
  const prefixArmedRef = useRef<boolean>(false);
  const prefixTimerRef = useRef<number | null>(null);
  const flashTimerRef = useRef<number | null>(null);
  const activeRef = useRef<PaneId>(initial);

  useEffect(() => { activeRef.current = active; }, [active]);

  const disarm = useCallback(() => {
    prefixArmedRef.current = false;
    if (prefixTimerRef.current !== null) {
      window.clearTimeout(prefixTimerRef.current);
      prefixTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      // Step 1: detect the prefix itself.
      // Ctrl+B (no Cmd, no Alt — we don't want to fight macOS shortcuts).
      const isPrefix = e.ctrlKey && !e.metaKey && !e.altKey && e.key.toLowerCase() === 'b';
      if (isPrefix) {
        e.preventDefault();
        prefixArmedRef.current = true;
        setPrefixActive(true);
        // The dot flashes for 200ms even if the user hasn't pressed the second
        // key yet. The "armed" state itself stays alive for the full 500ms.
        if (flashTimerRef.current !== null) window.clearTimeout(flashTimerRef.current);
        flashTimerRef.current = window.setTimeout(() => {
          setPrefixActive(false);
          flashTimerRef.current = null;
        }, PREFIX_FLASH_MS);
        if (prefixTimerRef.current !== null) window.clearTimeout(prefixTimerRef.current);
        prefixTimerRef.current = window.setTimeout(() => {
          prefixArmedRef.current = false;
          prefixTimerRef.current = null;
        }, PREFIX_TIMEOUT_MS);
        return;
      }

      // Step 2: if the prefix is armed, interpret the next keystroke.
      if (prefixArmedRef.current) {
        const k = e.key;
        const lower = k.toLowerCase();
        let handled = true;
        const curr = activeRef.current;
        if (k === 'ArrowLeft')      setActive(paneInDirection(curr, 'left'));
        else if (k === 'ArrowRight') setActive(paneInDirection(curr, 'right'));
        else if (k === 'ArrowUp')    setActive(paneInDirection(curr, 'up'));
        else if (k === 'ArrowDown')  setActive(paneInDirection(curr, 'down'));
        else if (k === '0')          setActive('coach');
        else if (k === '1')          setActive('tech');
        else if (k === '2')          setActive('shell');
        else if (lower === 'o') {
          const idx = PANE_ORDER.indexOf(curr);
          setActive(PANE_ORDER[(idx + 1) % PANE_ORDER.length]);
        } else {
          handled = false;
        }
        if (handled) {
          e.preventDefault();
          e.stopPropagation();
          disarm();
        }
      }
    }

    window.addEventListener('keydown', onKeyDown, true /* capture, so we beat the chat inputs */);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      if (prefixTimerRef.current !== null) window.clearTimeout(prefixTimerRef.current);
      if (flashTimerRef.current !== null) window.clearTimeout(flashTimerRef.current);
    };
  }, [disarm]);

  return { active, setActive, prefixActive };
}
